import { StyleSheet, css } from 'aphrodite'
import { useCallback, useEffect, useState } from 'react'
import DatePicker from './DatePicker'

interface DateRangeFilterProps {
  dateFrom: Date | null
  dateTo: Date | null
  onDateFromChange: (date: Date | null) => void
  onDateToChange: (date: Date | null) => void
  onClear: () => void
  showYearMonthDropdowns?: boolean
}

const currentYear = new Date().getFullYear()
const yearOptions = Array.from({ length: 6 }, (_, i) => currentYear - i)
const monthOptions = [
  { value: 0, label: 'January' },
  { value: 1, label: 'February' },
  { value: 2, label: 'March' },
  { value: 3, label: 'April' },
  { value: 4, label: 'May' },
  { value: 5, label: 'June' },
  { value: 6, label: 'July' },
  { value: 7, label: 'August' },
  { value: 8, label: 'September' },
  { value: 9, label: 'October' },
  { value: 10, label: 'November' },
  { value: 11, label: 'December' },
]

const DateRangeFilter: React.FC<DateRangeFilterProps> = ({
  dateFrom,
  dateTo,
  onDateFromChange,
  onDateToChange,
  onClear,
  showYearMonthDropdowns = false,
}) => {
  const [showFromPicker, setShowFromPicker] = useState(false)
  const [showToPicker, setShowToPicker] = useState(false)
  const [selectedYear, setSelectedYear] = useState<number | null>(null)
  const [selectedMonth, setSelectedMonth] = useState<number | null>(null)

  // Handler to set date range when year+month selected
  const handleYearMonthChange = useCallback(
    (year: number | null, month: number | null) => {
      if (year !== null && month !== null) {
        const startDate = new Date(year, month, 1)
        const endDate = new Date(year, month + 1, 0) // Last day of month
        onDateFromChange(startDate)
        onDateToChange(endDate)
      }
    },
    [onDateFromChange, onDateToChange],
  )

  // Update date range when year or month changes
  useEffect(() => {
    handleYearMonthChange(selectedYear, selectedMonth)
  }, [selectedYear, selectedMonth, handleYearMonthChange])

  // Clear year/month dropdowns when clearing date filter
  const handleClear = () => {
    setSelectedYear(null)
    setSelectedMonth(null)
    onClear()
  }

  const formatDate = (date: Date | null) => {
    if (!date) return ''
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    })
  }

  const hasActiveFilter = dateFrom || dateTo

  return (
    <div className={css(styles.container)}>
      <div className={css(styles.filterGroup)}>
        <div className={css(styles.dateInputs)}>
          {showYearMonthDropdowns && (
            <>
              <select
                className={css(styles.dropdown)}
                value={selectedYear ?? ''}
                onChange={(e) =>
                  setSelectedYear(
                    e.target.value ? Number(e.target.value) : null,
                  )
                }
              >
                <option value="">Year</option>
                {yearOptions.map((year) => (
                  <option key={year} value={year}>
                    {year}
                  </option>
                ))}
              </select>

              <select
                className={css(styles.dropdown)}
                value={selectedMonth ?? ''}
                onChange={(e) =>
                  setSelectedMonth(
                    e.target.value !== '' ? Number(e.target.value) : null,
                  )
                }
              >
                <option value="">Month</option>
                {monthOptions.map((month) => (
                  <option key={month.value} value={month.value}>
                    {month.label}
                  </option>
                ))}
              </select>

              <span className={css(styles.dateSeparator)}>or</span>
            </>
          )}

          <div className={css(styles.dateInput)}>
            <button
              className={css(styles.dateButton)}
              onClick={() => setShowFromPicker(true)}
            >
              <i className="fa-solid fa-calendar-days" />
              {dateFrom ? formatDate(dateFrom) : 'From Date'}
            </button>
          </div>

          <span className={css(styles.dateSeparator)}>to</span>

          <div className={css(styles.dateInput)}>
            <button
              className={css(styles.dateButton)}
              onClick={() => setShowToPicker(true)}
            >
              <i className="fa-solid fa-calendar-days" />
              {dateTo ? formatDate(dateTo) : 'To Date'}
            </button>
          </div>

          {hasActiveFilter && (
            <button
              className={css(styles.clearButton)}
              onClick={handleClear}
              title="Clear date filter"
            >
              <i className="fa-solid fa-times" />
            </button>
          )}
        </div>
      </div>

      <DatePicker
        selected={dateFrom}
        onChange={onDateFromChange}
        onSelect={() => setShowFromPicker(false)}
        minDate={new Date('2020-01-01')} // Allow past dates for filtering
        isOpen={showFromPicker}
        onClose={() => setShowFromPicker(false)}
      />

      <DatePicker
        selected={dateTo}
        onChange={onDateToChange}
        onSelect={() => setShowToPicker(false)}
        minDate={dateFrom || new Date('2020-01-01')} // To date should be after from date
        isOpen={showToPicker}
        onClose={() => setShowToPicker(false)}
      />
    </div>
  )
}

const styles = StyleSheet.create({
  container: {
    position: 'relative',
  },
  filterGroup: {
    display: 'flex',
    flexDirection: 'column',
    gap: '8px',
  },
  dateInputs: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    flexWrap: 'wrap',
  },
  dateInput: {
    position: 'relative',
  },
  dateButton: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    padding: '8px 12px',
    border: '1px solid #D1D5DB',
    borderRadius: '6px',
    backgroundColor: 'white',
    cursor: 'pointer',
    fontSize: '14px',
    color: '#374151',
    minWidth: '140px',
    justifyContent: 'flex-start',
    ':hover': {
      borderColor: '#1C64F2',
      backgroundColor: '#F9FAFB',
    },
    ':focus': {
      outline: 'none',
      borderColor: '#1C64F2',
      boxShadow: '0 0 0 2px rgba(28, 100, 242, 0.1)',
    },
  },
  dateSeparator: {
    fontSize: '14px',
    color: '#6B7280',
    fontWeight: 500,
  },
  clearButton: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: '16px',
    height: '16px',
    cursor: 'pointer',
    color: '#6B7280',
    ':hover': {
      color: '#EF4444',
    },
  },
  dropdown: {
    padding: '8px 12px',
    border: '1px solid #D1D5DB',
    borderRadius: '6px',
    backgroundColor: 'white',
    cursor: 'pointer',
    fontSize: '14px',
    color: '#374151',
    minWidth: '100px',
    ':hover': {
      borderColor: '#1C64F2',
      backgroundColor: '#F9FAFB',
    },
    ':focus': {
      outline: 'none',
      borderColor: '#1C64F2',
      boxShadow: '0 0 0 2px rgba(28, 100, 242, 0.1)',
    },
  },
})

export default DateRangeFilter
